"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { motion } from "framer-motion";
import { PrismicNextImage } from "@prismicio/next";

/**
 * Props for `GetInTouchSection`.
 */
export type GetInTouchSectionProps =
  SliceComponentProps<Content.GetInTouchSectionSlice>;

/**
 * Component for "GetInTouchSection" Slices.
 */
const GetInTouchSection: FC<GetInTouchSectionProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#F8FCFF] py-[60px] px-4 md:px-[30px] lg:px-[60px] xl:px-20"
    >
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <PrismicRichText
          field={slice.primary.title}
          components={{
            label: ({ node, children }) => {
              if (node.data.label === "Highlight") {
                return <span className="text-secondary">{children}</span>;
              }
              return <span className="text-primary">{children}</span>;
            },
            heading1: ({ children }) => (
              <h1 className="font-archivo text-[#033D61] text-5xl font-semibold leading-16 text-center mb-8">{children}</h1>
            ),
            heading2: ({ children }) => (
              <h2 className="font-archivo text-[#033D61] text-5xl font-semibold leading-16 text-center mb-8">{children}</h2>
            ),
            heading3: ({ children }) => (
              <h3 className="font-archivo text-[#033D61] text-5xl font-semibold leading-16 text-center mb-8">{children}</h3>
            ),
            heading4: ({ children }) => (
              <h4 className="font-archivo text-[#033D61] text-5xl font-semibold leading-16 text-center mb-8">{children}</h4>
            ),
            heading5: ({ children }) => (
              <h5 className="font-archivo text-[#033D61] text-5xl font-semibold leading-16 text-center mb-8">{children}</h5>
            ),
            heading6: ({ children }) => (
              <h6 className="font-archivo text-[#033D61] text-5xl font-semibold leading-16 text-center mb-8">{children}</h6>
            ),
          }}
        />
      </motion.div>
      <motion.div
        className="flex items-start gap-[105px] flex-col xl:flex-row justify-between"
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="flex items-start gap-4 justify-center flex-1">
          <div className="h-16 w-16 rounded-full bg-[#EBF7FF] flex items-center justify-center shrink-0">
            <PrismicNextImage field={slice.primary.phone_number_icon} />
          </div>
          <div className="space-y-2">
            <div className="font-archivo text-[#033D61] text-base font-normal leading-6">
              {slice.primary.phone_number_label}
            </div>
            <div className="text-[#033D61] font-archivo text-2xl font-semibold leading-9">
              {slice.primary.phone_number_value}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-4 justify-center flex-1">
          <div className="h-16 w-16 rounded-full bg-[#EBF7FF] flex items-center justify-center shrink-0">
            <PrismicNextImage field={slice.primary.email_icon} />
          </div>
          <div className="space-y-2">
            <div className="font-archivo text-[#033D61] text-base font-normal leading-6">
              {slice.primary.email_label}
            </div>
            <div className="text-[#033D61] font-archivo text-2xl font-semibold leading-9">
              {slice.primary.email_value}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-4 justify-center flex-1">
          <div className="h-16 w-16 rounded-full bg-[#EBF7FF] flex items-center justify-center shrink-0">
            <PrismicNextImage field={slice.primary.location_icon} />
          </div>
          <div className="space-y-2">
            <div className="font-archivo text-[#033D61] text-base font-normal leading-6">
              {slice.primary.location_label}
            </div>
            <div className="text-[#033D61] font-archivo text-2xl font-semibold leading-9">
              {slice.primary.location_value}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default GetInTouchSection;
